import { useCallback, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Fade } from 'react-awesome-reveal';
import clsx from 'clsx';

// Imports => Constants
import {
  AcBadge,
  DATETIME_FORMATS,
  ICONS,
  KEYS,
  SIZES,
  THEMES,
} from '@opp/ui-kit';

// Imports => Utilities
import {
  AcIsSet,
  AcSanitize,
} from '@opp/ui-kit';
import {
  AcFormatCurrencyAmount,
  AcFormatDate,
  AcFormatInternalURI,
} from '@utils';

// Imports => Atoms
import {
  AcCard,
  AcHeading,
  AcRichContent,
  AcIcon,
  AcRipple,
} from '@opp/ui-kit';
import {withTranslation} from "react-i18next";
import {AcFormatPaymentMethod, AcGetPaymentMethodIcon, AcGetPaymentMethodImg} from "../../utilities/ac-format-payment-method";

const _CLASSES = {
  MAIN: 'ac-transaction-list-item',
  LINK: 'ac-transaction-list-item__link',
  HEADER: 'ac-transaction-list-item__header',
  BODY: 'ac-transaction-list-item__body',
  FOOTER: 'ac-transaction-list-item__footer',
  ICON: 'ac-transaction-list-item__icon',
  PAYMENT_ICON: 'ac-transaction-list-item__payment-icon',
  TITLE: 'ac-transaction-list-item__title',
  DESCRIPTION: 'ac-transaction-list-item__description',
  AMOUNT: 'ac-transaction-list-item__amount',
  DATETIME: 'ac-transaction-list-item__datetime',
  STATUS: 'ac-transaction-list-item__status',
  UID: 'ac-transaction-list-item__uid',
};

const AcTransactionListItem = ({ data = {}, className, i18n, t }) => {
  const getStatusClassNames = useMemo(() => {
    return clsx(_CLASSES.STATUS);
  }, []);

  const getDatetimeClassNames = useMemo(() => {
    return clsx(_CLASSES.DATETIME);
  }, []);

  const getPaymentIconClassNames = useMemo(() => {
    return clsx(_CLASSES.PAYMENT_ICON);
  }, []);

  const getDescriptionClassNames = useMemo(() => {
    return clsx(_CLASSES.DESCRIPTION);
  }, []);

  const getAmountClassNames = useMemo(() => {
    return clsx(_CLASSES.AMOUNT);
  }, []);

  const getTitleClassNames = useMemo(() => {
    return clsx(_CLASSES.TITLE);
  }, []);

  const getIconClassNames = useCallback((type) => {
    return clsx(_CLASSES.ICON, `${_CLASSES.ICON}--${type}`);
  }, []);

  const getFooterClassNames = useMemo(() => {
    return clsx(_CLASSES.FOOTER);
  }, []);

  const getBodyClassNames = useMemo(() => {
    return clsx(_CLASSES.BODY);
  }, []);

  const getHeaderClassNames = useMemo(() => {
    return clsx(_CLASSES.HEADER);
  }, []);

  const getLinkClassNames = useMemo(() => {
    return clsx(_CLASSES.LINK);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN, className);
  }, [className]);

  const getID = useMemo(() => {
    return data && data.uid;
  }, [data]);

  const getObject = useMemo(() => {
    return data && data.object;
  }, [data]);

  const getPaymentMethodIcon = useMemo(() => {
    if (
      !AcIsSet(data) ||
      (!AcIsSet(data.payment_method) && !AcIsSet(data.parent_payment_method)) ||
      data.object === KEYS.REFUND
    ) {
      return null;
    }

    return AcGetPaymentMethodImg(
      t,
      getPaymentIconClassNames,
      data.payment_method,
      data.parent_payment_method
    );
  }, [data]);

  const getTypeAttributes = useMemo(() => {
    if (!AcIsSet(data)) return null;

    let result = {
      type: null,
      theme: null,
      icon: null,
      status: data.status,
    };

    const { category, type, object } = data;

    switch (category) {
      case KEYS.ESCROW:
        result.type = 'escrow';
        result.theme = THEMES.OMEGA_LIGHT;
        result.icon = ICONS.LOCK_OUTLINE;
        result.object = 'escrow';
        break;

      default:
        if (type === KEYS.INCOMING) {
          result.type = 'incoming';
          result.theme = THEMES.CHARLIE_LIGHT;
          result.icon = ICONS.EAST;
        } else if (type === KEYS.OUTGOING) {
          result.type = 'outgoing';
          result.theme = THEMES.ECHO;
          result.icon = ICONS.WEST;
        }

        result.object = object;
    }

    return result;
  }, [data]);

  const getStatusFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.status)) return null;
    if (!AcIsSet(getTypeAttributes)) return null;

    let { status } = data;
    // Only refunds will have status created -> we show them as completed.
    if (status === 'created') {
      status = 'completed';
    }

    let label = t('status.' + status,  {ns: 'transactions'});
    let theme = null;

    switch (status) {
      case 'created':
      case 'reserved':
      case 'pending':
      case 'planned':
        theme = THEMES.OMEGA_LIGHT;
        break;

      case 'on_hold':
      case 'refunded':
        theme = THEMES.ECHO;
        break;

      case 'chargeback':
        theme = THEMES.BETA_LIGHT;
        break;

      case 'expired':
      case 'cancelled':
      case 'failed':
      case 'revoked':
        theme = THEMES.ERROR;
        break;

      case 'completed':
        theme = THEMES.SUCCESS;
        break;

      default:
    }

    return <AcBadge className={getStatusClassNames} theme={theme}>{label}</AcBadge>;
  }, [data]);

  const getCreatedDateFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.created)) return null;

    const created = data.created * 1000;
    const result = AcFormatDate(
      created,
      null,
      DATETIME_FORMATS.FULL_DAY_WITH_YEAR_AND_TIME_SHORT
    );

    return <AcRichContent className={getDatetimeClassNames} content={result} />;
  }, [data, i18n.resolvedLanguage]);

  const getDescriptionFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.description)) return null;

    const { description } = data;

    return (
      <AcRichContent className={getDescriptionClassNames} content={AcSanitize(description)} />
    );
  }, [data]);

  const getAmountFormatted = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.amount)) return null;

    const { amount, currency } = data;

    const content = AcFormatCurrencyAmount(amount, currency, i18n.resolvedLanguage, true);

    return <AcRichContent content={content} className={getAmountClassNames} />;
  }, [data, i18n.resolvedLanguage]);

  const getTitleFormatted = useMemo(() => {
    if (!AcIsSet(data)) return null;
    if (!AcIsSet(getTypeAttributes)) return null;

    const { object } = getTypeAttributes;

    return (
      <AcHeading rank={6} className={getTitleClassNames}>
        {`${t('type.' + object,  {ns: 'transactions'})}`}
      </AcHeading>
    );
  }, [data, getTypeAttributes]);

  const getIconFormatted = useMemo(() => {
    if (!AcIsSet(data)) return null;
    if (!AcIsSet(getTypeAttributes)) return null;

    const { type, icon } = getTypeAttributes;

    if (!AcIsSet(icon)) return null;

    return (
      <AcIcon icon={icon} className={getIconClassNames(type.toLowerCase())} />
    );
  }, [getTypeAttributes]);

  const getRoute = useMemo(() => {
    if (!AcIsSet(getID)) {
      return null;
    }
    if (!AcIsSet(getObject)) {
      return null;
    }

    let type;

    switch (getObject) {
      case KEYS.TRANSACTION:
      case KEYS.CHARGEBACK:
        type = KEYS.TRANSACTION
        break;
      case KEYS.REFUND:
      case KEYS.INTERNAL_TRANSFER:
        type = KEYS.MOVEMENT
        break;
      case KEYS.WITHDRAWAL:
        type = KEYS.WITHDRAWAL
        break;
      default:
        type = KEYS.TRANSACTION
    }

    return AcFormatInternalURI(data, {
      field: KEYS.UID,
      type: type,
    });
  }, [getID, getObject, data]);

  return (
    <Fade direction={'up'} duration={300} triggerOnce>
      <AcCard
        className={getMainClassNames}
        id={getID}
        data-payment-method={data && data.payment_method}
      >
        <AcRipple theme={THEMES.SUBTLE} size={SIZES.SMALL} simple />
        <Link
          to={getRoute}
          title={`${t('click-for-details', 'Click to view details of transaction', {ns: 'transactions'})} ${getID}`}
          className={getLinkClassNames}
        >
          <div className={getHeaderClassNames}>
            {getIconFormatted}
            {getTitleFormatted}
            {getAmountFormatted}
          </div>
          <div className={getBodyClassNames}>
            {getDescriptionFormatted}
            {getPaymentMethodIcon}
          </div>
          <div className={getFooterClassNames}>
            {getCreatedDateFormatted}
            {getStatusFormatted}
          </div>
        </Link>
      </AcCard>
    </Fade>
  );
};

AcTransactionListItem.propTypes = {
  /**
     * For CSS customization
     */
  className: PropTypes.string,
};

export default withTranslation(['transactions', 'common'])(memo(AcTransactionListItem));
